import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Check, X, Building2, MapPin, Loader2 } from "lucide-react";
import {
  fetchPendingUniversities,
  approveUniversity,
  rejectUniversity,
  PendingUniversity,
} from "@/services/pendingUniversityService";

const PendingUniversitiesTable: React.FC = () => {
  const [pendingUniversities, setPendingUniversities] = useState<PendingUniversity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedUniversity, setSelectedUniversity] = useState<PendingUniversity | null>(null);
  const [rejectNotes, setRejectNotes] = useState("");

  const loadPendingUniversities = async () => {
    setIsLoading(true);
    const data = await fetchPendingUniversities();
    setPendingUniversities(data);
    setIsLoading(false);
  };

  useEffect(() => {
    loadPendingUniversities();
  }, []);

  const handleApprove = async (university: PendingUniversity) => {
    setProcessingId(university.id);
    const result = await approveUniversity(university);
    
    if (result) {
      toast.success(`University "${university.name}" has been approved`);
      loadPendingUniversities();
    } else {
      toast.error("Failed to approve university");
    }
    setProcessingId(null);
  };

  const handleRejectClick = (university: PendingUniversity) => {
    setSelectedUniversity(university);
    setRejectNotes("");
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async () => {
    if (!selectedUniversity) return;

    setProcessingId(selectedUniversity.id);
    const success = await rejectUniversity(selectedUniversity.id, rejectNotes);
    
    if (success) {
      toast.success(`University "${selectedUniversity.name}" has been rejected`);
      loadPendingUniversities();
    } else {
      toast.error("Failed to reject university");
    }
    
    setProcessingId(null);
    setRejectDialogOpen(false);
    setSelectedUniversity(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (pendingUniversities.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Building2 className="h-12 w-12 mx-auto mb-2 opacity-50" />
        <p>No pending university submissions</p>
      </div>
    );
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>University</TableHead>
            <TableHead>Location</TableHead>
            <TableHead>Submitted By</TableHead>
            <TableHead>Date</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {pendingUniversities.map((university) => (
            <TableRow key={university.id}>
              <TableCell>
                <div className="font-medium">{university.name}</div>
                {university.address && (
                  <div className="text-sm text-muted-foreground">{university.address}</div>
                )}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-1">
                  <MapPin className="h-3 w-3 text-muted-foreground" />
                  <span>{university.city}, {university.country}</span>
                </div>
                {university.postcode && (
                  <div className="text-sm text-muted-foreground">{university.postcode}</div>
                )}
              </TableCell>
              <TableCell>
                <div>{university.submitter_name || "Unknown"}</div>
                <div className="text-sm text-muted-foreground">{university.submitter_email}</div>
              </TableCell>
              <TableCell>
                {new Date(university.created_at).toLocaleDateString()}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-green-600 hover:text-green-700 hover:bg-green-50"
                    onClick={() => handleApprove(university)}
                    disabled={processingId === university.id}
                  >
                    {processingId === university.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Check className="h-4 w-4" />
                    )}
                    <span className="ml-1">Approve</span>
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => handleRejectClick(university)}
                    disabled={processingId === university.id}
                  >
                    <X className="h-4 w-4" />
                    <span className="ml-1">Reject</span>
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject University</DialogTitle>
            <DialogDescription>
              Are you sure you want to reject "{selectedUniversity?.name}"? You can optionally provide a reason.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="reject-notes">Rejection Notes (optional)</Label>
              <Textarea
                id="reject-notes"
                value={rejectNotes}
                onChange={(e) => setRejectNotes(e.target.value)}
                placeholder="Reason for rejection..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleRejectConfirm}>
              Reject University
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PendingUniversitiesTable;
